import { supabase } from '../lib/supabase';

/**
 * Service for Student Roles Discovery Page
 * Handles fetching roles data with categorization:
 * - Continue Learning (roles from songs user is working on)
 * - Popular Roles
 * - Recently Updated Roles
 * - Recently Added Roles
 */

export const studentRolesDiscoveryService = {
  /**
   * Get roles the user is currently learning (from songs in progress)
   */
  async getContinueLearningRoles(userId, limit = 12) {
    try {
      // Get songs user is working on
      const { data: progressData, error: progressError } = await supabase?.from('user_lesson_progress')?.select('song_id')?.eq('user_id', userId)?.in('lesson_status', ['started', 'available'])?.limit(50);

      if (progressError) throw progressError;

      const songIds = progressData?.map(p => p?.song_id)?.filter(Boolean) || [];

      if (songIds?.length === 0) return [];

      // Get roles associated with these songs via song_roles table
      const { data: songRoles, error: songRolesError } = await supabase?.from('song_roles')?.select('role_id')?.in('song_id', songIds);

      if (songRolesError) throw songRolesError;

      const roleIds = [...new Set(songRoles?.map(sr => sr?.role_id)?.filter(Boolean))];

      if (roleIds?.length === 0) return [];

      // Fetch role details with musical info
      const { data: roles, error: rolesError } = await supabase?.from('roles')?.select(`
          id,
          character_name,
          voice_type,
          vocal_range,
          description,
          musical_id,
          musicals (
            title,
            poster_url
          )
        `)?.in('id', roleIds)?.limit(limit);

      if (rolesError) throw rolesError;

      // Get song count for each role
      const roleData = await Promise.all(
        (roles || [])?.map(async (role) => {
          const { count } = await supabase?.from('song_roles')?.select('*', { count: 'exact', head: true })?.eq('role_id', role?.id);

          return {
            id: role?.id,
            characterName: role?.character_name,
            voiceType: role?.voice_type,
            vocalRange: role?.vocal_range,
            description: role?.description,
            musicalId: role?.musical_id,
            musicalTitle: role?.musicals?.title,
            musicalPoster: role?.musicals?.poster_url,
            songCount: count || 0
          };
        })
      );

      return roleData;
    } catch (error) {
      console.error('Error fetching continue learning roles:', error);
      throw error;
    }
  },

  /**
   * Get popular roles (most favorited)
   */
  async getPopularRoles(limit = 12) {
    try {
      // Count favorites per role
      const { data: favorites, error: favError } = await supabase?.from('user_role_favorites')?.select('role_id');

      if (favError) throw favError;

      // Count occurrences
      const counts = {};
      favorites?.forEach(fav => {
        counts[fav?.role_id] = (counts?.[fav?.role_id] || 0) + 1;
      });

      // Sort by count and get top role IDs
      const topRoleIds = Object.entries(counts)?.sort(([, a], [, b]) => b - a)?.slice(0, limit * 2)?.map(([id]) => id);

      if (topRoleIds?.length === 0) {
        // Fallback to recently added roles
        const { data: allRoles, error: allError } = await supabase?.from('roles')?.select(`
            id,
            character_name,
            voice_type,
            vocal_range,
            description,
            musical_id,
            musicals (
              title,
              poster_url
            )
          `)?.order('created_at', { ascending: false })?.limit(limit);

        if (allError) throw allError;

        const roleData = await Promise.all(
          (allRoles || [])?.map(async (role) => {
            const { count } = await supabase?.from('song_roles')?.select('*', { count: 'exact', head: true })?.eq('role_id', role?.id);

            return {
              id: role?.id,
              characterName: role?.character_name,
              voiceType: role?.voice_type,
              vocalRange: role?.vocal_range,
              description: role?.description,
              musicalId: role?.musical_id,
              musicalTitle: role?.musicals?.title,
              musicalPoster: role?.musicals?.poster_url,
              songCount: count || 0
            };
          })
        );

        return roleData;
      }

      const { data: roles, error: rolesError } = await supabase?.from('roles')?.select(`
          id,
          character_name,
          voice_type,
          vocal_range,
          description,
          musical_id,
          musicals (
            title,
            poster_url
          )
        `)?.in('id', topRoleIds)?.limit(limit);

      if (rolesError) throw rolesError;

      const roleData = await Promise.all(
        (roles || [])?.map(async (role) => {
          const { count } = await supabase?.from('song_roles')?.select('*', { count: 'exact', head: true })?.eq('role_id', role?.id);

          return {
            id: role?.id,
            characterName: role?.character_name,
            voiceType: role?.voice_type,
            vocalRange: role?.vocal_range,
            description: role?.description,
            musicalId: role?.musical_id,
            musicalTitle: role?.musicals?.title,
            musicalPoster: role?.musicals?.poster_url,
            songCount: count || 0
          };
        })
      );

      return roleData;
    } catch (error) {
      console.error('Error fetching popular roles:', error);
      throw error;
    }
  },

  /**
   * Get recently updated roles
   */
  async getRecentlyUpdatedRoles(limit = 12) {
    try {
      const { data, error } = await supabase?.from('roles')?.select(`
          id,
          character_name,
          voice_type,
          vocal_range,
          description,
          musical_id,
          updated_at,
          musicals (
            title,
            poster_url
          )
        `)?.order('updated_at', { ascending: false })?.limit(limit);

      if (error) throw error;

      const roleData = await Promise.all(
        (data || [])?.map(async (role) => {
          const { count } = await supabase?.from('song_roles')?.select('*', { count: 'exact', head: true })?.eq('role_id', role?.id);

          return {
            id: role?.id,
            characterName: role?.character_name,
            voiceType: role?.voice_type,
            vocalRange: role?.vocal_range,
            description: role?.description,
            musicalId: role?.musical_id,
            musicalTitle: role?.musicals?.title,
            musicalPoster: role?.musicals?.poster_url,
            songCount: count || 0
          };
        })
      );

      return roleData;
    } catch (error) {
      console.error('Error fetching recently updated roles:', error);
      throw error;
    }
  },

  /**
   * Get recently added roles
   */
  async getRecentlyAddedRoles(limit = 12) {
    try {
      const { data, error } = await supabase?.from('roles')?.select(`
          id,
          character_name,
          voice_type,
          vocal_range,
          description,
          musical_id,
          created_at,
          musicals (
            title,
            poster_url
          )
        `)?.order('created_at', { ascending: false })?.limit(limit);

      if (error) throw error;

      const roleData = await Promise.all(
        (data || [])?.map(async (role) => {
          const { count } = await supabase?.from('song_roles')?.select('*', { count: 'exact', head: true })?.eq('role_id', role?.id);

          return {
            id: role?.id,
            characterName: role?.character_name,
            voiceType: role?.voice_type,
            vocalRange: role?.vocal_range,
            description: role?.description,
            musicalId: role?.musical_id,
            musicalTitle: role?.musicals?.title,
            musicalPoster: role?.musicals?.poster_url,
            songCount: count || 0
          };
        })
      );

      return roleData;
    } catch (error) {
      console.error('Error fetching recently added roles:', error);
      throw error;
    }
  },

  /**
   * Search roles
   */
  async searchRoles(query) {
    try {
      const { data, error } = await supabase?.from('roles')?.select(`
          id,
          character_name,
          voice_type,
          vocal_range,
          description,
          musical_id,
          musicals (
            title,
            poster_url
          )
        `)?.ilike('character_name', `%${query}%`)?.order('character_name', { ascending: true })?.limit(20);

      if (error) throw error;

      const roleData = await Promise.all(
        (data || [])?.map(async (role) => {
          const { count } = await supabase?.from('song_roles')?.select('*', { count: 'exact', head: true })?.eq('role_id', role?.id);

          return {
            id: role?.id,
            characterName: role?.character_name,
            voiceType: role?.voice_type,
            vocalRange: role?.vocal_range,
            description: role?.description,
            musicalId: role?.musical_id,
            musicalTitle: role?.musicals?.title,
            musicalPoster: role?.musicals?.poster_url,
            songCount: count || 0
          };
        })
      );

      return roleData;
    } catch (error) {
      console.error('Error searching roles:', error);
      throw error;
    }
  }
};